import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { Icon } from '@iconify/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { colors } from 'theme/colors';

import { Select } from 'components/data-entry/Select';

import { Workspace } from 'store/workspace.store';

interface WorkspaceSelectorProps {
  options: Workspace[];
  value: Workspace | null;
  onChange: (value: Workspace) => void;
  placeholder?: string;
}

export function WorkSpaceSelector({
  options,
  value,
  onChange,
  placeholder = 'Select workspace',
}: WorkspaceSelectorProps) {
  const [open, setOpen] = useState(false);

  const selectOptions = useMemo(
    () =>
      options?.map((option) => ({
        value: option?.id || '',
        label: option?.name || '',
      })),
    [options]
  );

  const handleWorkspaceChange = useCallback(
    (newValue: string | number | (string | number)[] | null) => {
      if (typeof newValue !== 'string') return;
      const selected = options.find((opt) => opt?.id === newValue);
      if (selected) {
        onChange(selected);
      }
    },
    [options, onChange]
  );

  const onlyOneOption = useMemo(() => {
    return selectOptions?.length === 1;
  }, [selectOptions]);

  useEffect(() => {
    if (!selectOptions?.find((opt) => opt.value === value?.id)) {
      handleWorkspaceChange(selectOptions[0]?.value);
    }
  }, [value, selectOptions, handleWorkspaceChange]);

  return (
    <Select
      variant={onlyOneOption ? 'borderless' : 'outlined'}
      value={value?.id}
      data-testid="workspace-selector"
      className={'min-w-[200px] min-h-[40px]'}
      options={selectOptions}
      onChange={handleWorkspaceChange}
      placeholder={placeholder}
      suffixIcon={
        onlyOneOption ? null : open ? (
          <UpOutlined className="text-white" />
        ) : (
          <DownOutlined className="text-white" />
        )
      }
      classNames={{
        popup: {
          root: 'workspace-selector-popup',
          listItem: 'workspace-selector-list-item',
        },
      }}
      open={onlyOneOption ? false : open}
      fullWidth
      onOpenChange={setOpen}
      styles={{
        popup: {
          root: {
            border: `1px solid ${colors.primaryGreen6}`,
          },
        },
        root: {
          minHeight: 40,
        },
      }}
      prefix={
        <Icon
          icon="ant-design:database-outlined"
          className="text-primary bg-lime5 p-1 rounded-md h-6 w-6"
        />
      }
      optionRender={(option) => (
        <div className="flex items-center gap-2">
          <Icon icon="ant-design:database-outlined" className="text-gray-500" />
          <span>{option.label}</span>
        </div>
      )}
    />
  );
}
